// The admin accounts allowed to see and drive the dev tools (simulate / finalize / reset) and the
// backoffice. Keep this as the one source of truth — do not scatter the literal
// email around the codebase.

/**
 * The bootstrap admin: the fallback when the environment names nobody.
 *
 * Deliberately a placeholder at an unroutable domain (RFC 2606 reserves `example.com`, so nobody
 * can ever own this address) rather than a real person's email — the repo should not carry a
 * personal address, and a deploy that names no admin should grant admin to nobody rather than to
 * whoever happens to be hardcoded. Real admins come from `ADMIN_EMAILS` / `VITE_ADMIN_EMAILS`;
 * see .env.example. Tests use this constant as their admin fixture.
 */
export const ADMIN_EMAIL = 'admin@example.com'

/**
 * The env shape both sides can satisfy: `process.env` on the server, Vite's `import.meta.env` in
 * the browser. Deliberately one implementation with the env as a parameter, rather than two that
 * can drift — a client that thinks you are an admin while the server does not
 * (or the reverse) is a screen that renders and then 403s on every fetch.
 */
export type AdminEnv = Record<string, string | undefined>

/**
 * Vite only exposes `VITE_`-prefixed variables to the browser bundle, so a deployment that wants
 * the client to agree with the server sets both spellings. Reading both here is what lets the
 * single implementation above serve both call sites.
 */
const ADMIN_EMAILS_KEYS = ['ADMIN_EMAILS', 'VITE_ADMIN_EMAILS'] as const

function defaultEnv(): AdminEnv {
  // `process.env` first, and read fresh on every call rather than captured once.
  // Bun aliases `import.meta.env` to the env object that existed at startup — an *alias*, not a
  // live view: code that assigns a whole new object (`process.env = { ...previous }`, which two
  // suites here do to scope a variable) leaves `import.meta.env` pointing at the abandoned one,
  // and every later write is invisible to it. Reading `process.env` follows the reassignment.
  // The browser has no `process`, so it falls through to `import.meta.env` as before.
  if (typeof process !== 'undefined' && process.env) return process.env as AdminEnv
  return import.meta.env as unknown as AdminEnv
}

/**
 * Comma-separated `ADMIN_EMAILS`, falling back to the single bootstrap admin.
 *
 * Trimmed, lowercased, empty entries dropped, duplicates collapsed. Unset or empty answers
 * `[ADMIN_EMAIL]`, so a deployment that sets nothing behaves exactly as it did before this ticket.
 */
export function adminEmails(env: AdminEnv = defaultEnv()): string[] {
  const raw = ADMIN_EMAILS_KEYS.map((key) => env?.[key]).find(
    (value) => typeof value === 'string' && value.trim() !== ''
  )
  if (!raw) return [ADMIN_EMAIL]
  const parsed = [
    ...new Set(
      raw
        .split(',')
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) => entry !== '')
    ),
  ]
  return parsed.length > 0 ? parsed : [ADMIN_EMAIL]
}

/**
 * Case-insensitive so a differently-cased sign-in still resolves to admin.
 *
 * The signature is fixed: it is the one function everything calls — the client's
 * `identity.isAdmin`, every admin-gated router, and `resolveBypassUser` in server/auth.ts — and
 * nothing above it needed to change to gain a list.
 */
export function isAdminEmail(email: string | null | undefined): boolean {
  if (!email) return false
  return adminEmails().includes(email.trim().toLowerCase())
}
